/**
 * BigCommerce Checkout Observer
 * Helpers for detecting and monitoring BigCommerce optimized one-page checkout
 * Uses MutationObserver pattern recommended by BigCommerce dev team
 */
/**
 * Wait for a specific checkout step element to appear
 * Uses MutationObserver to watch for dynamic element creation
 */
export function waitForCheckoutStep(selector, timeoutMs = 30000) {
    return new Promise((resolve) => {
        // Check if element already exists
        const existing = document.querySelector(selector);
        if (existing) {
            console.debug(`Real ID :: Element ${selector} already exists`);
            resolve(existing);
            return;
        }
        console.debug(`Real ID :: Waiting for element ${selector}`);
        // Watch for element to appear
        const observer = new MutationObserver((mutations, obs) => {
            const element = document.querySelector(selector);
            if (element) {
                obs.disconnect();
                console.debug(`Real ID :: Element ${selector} appeared`);
                resolve(element);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        // Timeout after specified duration
        setTimeout(() => {
            observer.disconnect();
            console.debug(`Real ID :: Timeout waiting for ${selector} after ${timeoutMs}ms`);
            resolve(null);
        }, timeoutMs);
    });
}
/**
 * Check if we're on a BigCommerce checkout page
 * Detects checkout via window.checkoutConfig object
 */
export function isBcCheckoutPage() {
    const checkoutConfig = window.checkoutConfig;
    const hasCheckoutConfig = !!(checkoutConfig?.checkoutId &&
        document.getElementById('checkout-app'));
    console.debug(`Real ID :: BigCommerce checkout page detection: ${hasCheckoutConfig}`);
    return hasCheckoutConfig;
}
/**
 * Get checkout configuration from window
 */
export function getBcCheckoutConfig() {
    const config = window.checkoutConfig;
    if (!config)
        return null;
    return {
        checkoutId: config.checkoutId,
        containerId: config.containerId,
        publicPath: config.publicPath,
    };
}
/**
 * Wait for BigCommerce checkout SDK to initialize window.checkoutConfig
 * Uses polling since the SDK initializes asynchronously after our script runs
 *
 * @param timeoutMs - Maximum time to wait for checkoutConfig (default: 5000ms)
 * @returns The checkoutConfig object or null if timeout
 */
export function waitForBcCheckoutConfig(timeoutMs = 5000) {
    return new Promise((resolve) => {
        // Check if already available
        const existingConfig = window.checkoutConfig;
        if (existingConfig?.checkoutId) {
            console.debug("Real ID :: checkoutConfig already available");
            resolve(existingConfig);
            return;
        }
        console.debug(`Real ID :: Waiting for BC checkoutConfig (timeout: ${timeoutMs}ms)`);
        const startTime = Date.now();
        const interval = setInterval(() => {
            const config = window.checkoutConfig;
            if (config?.checkoutId) {
                clearInterval(interval);
                console.debug(`Real ID :: checkoutConfig available after ${Date.now() - startTime}ms`);
                resolve(config);
            }
            else if (Date.now() - startTime > timeoutMs) {
                clearInterval(interval);
                console.debug(`Real ID :: Timeout waiting for checkoutConfig after ${timeoutMs}ms`);
                resolve(null);
            }
        }, 100);
    });
}
/**
 * Checkout step element IDs from BigCommerce optimized checkout
 */
export const BC_CHECKOUT_STEPS = {
    customer: 'checkoutCustomer',
    shipping: 'checkoutShippingAddress',
    billing: 'checkoutBillingAddress',
    payment: 'checkoutPayment',
};
/**
 * Get the current checkout step based on visible elements
 */
export function getCurrentCheckoutStep() {
    const steps = [
        { id: BC_CHECKOUT_STEPS.customer, name: 'customer' },
        { id: BC_CHECKOUT_STEPS.shipping, name: 'shipping' },
        { id: BC_CHECKOUT_STEPS.billing, name: 'billing' },
        { id: BC_CHECKOUT_STEPS.payment, name: 'payment' },
    ];
    // Find the last visible step (most advanced in checkout flow)
    let currentStep = null;
    for (const step of steps) {
        const el = document.getElementById(step.id);
        if (el && el.offsetParent !== null) {
            currentStep = step.name;
        }
    }
    return currentStep;
}
/**
 * Hide/disable the payment section until verification is complete
 */
export function hidePaymentSection() {
    const paymentSection = document.getElementById(BC_CHECKOUT_STEPS.payment);
    if (paymentSection) {
        paymentSection.style.display = 'none';
        paymentSection.style.pointerEvents = 'none';
        console.debug('Real ID :: Payment section hidden');
    }
}
/**
 * Reveal the payment section after verification is complete
 * Note: Place Order button (#checkout-payment-continue) is shown/hidden via CSS
 * in PreCheckoutApp.tsx Helmet styles based on the `done` state
 */
export function showPaymentSection() {
    const paymentSection = document.getElementById(BC_CHECKOUT_STEPS.payment);
    if (paymentSection) {
        paymentSection.style.display = '';
        paymentSection.style.pointerEvents = '';
        console.debug('Real ID :: Payment section revealed');
    }
}
/**
 * Watch for checkout step changes
 * Calls callback when a new step becomes visible
 */
export function watchCheckoutSteps(callback) {
    const observer = new MutationObserver(() => {
        const currentStep = getCurrentCheckoutStep();
        if (currentStep) {
            const stepId = BC_CHECKOUT_STEPS[currentStep];
            const element = document.getElementById(stepId);
            if (element) {
                callback(currentStep, element);
            }
        }
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
    });
    // Return cleanup function
    return () => observer.disconnect();
}
